import React, { useState, useEffect } from 'react';
import { PieChartCard, ActivityChart } from './charts';

const AdminStatsTab = ({ stats, onRefresh, loading }) => {
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (stats && !loading) {
      setLastUpdated(new Date());
      setRefreshing(false);
    }
  }, [stats, loading]);

  const handleRefresh = async () => {
    if (onRefresh) {
      setRefreshing(true);
      await onRefresh();
    }
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    return lastUpdated.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !stats) {
    return (
      <div className="stats-loading">
        <div className="stats-grid">
          {[1, 2, 3].map(i => (
            <div key={i} className="stat-card skeleton">
              <div className="skeleton-text skeleton-title"></div>
              <div className="skeleton-text skeleton-value"></div>
              <div className="skeleton-text skeleton-breakdown"></div>
            </div>
          ))}
        </div>
        <div className="charts-section">
          <div className="chart-card skeleton chart-card-wide">
            <div className="skeleton-text skeleton-title"></div>
            <div className="skeleton-chart"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <>
      <div className="stats-header">
        <div className="stats-info">
          {lastUpdated && (
            <span className="last-updated">Last updated: {formatLastUpdated()}</span>
          )}
        </div>
        <button
          className="btn-refresh"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Primary Stats Row */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <h3>Total Users</h3>
          <div className="stat-value">{stats.users?.total || 0}</div>
          <div className="stat-breakdown">
            <span className="active">{stats.users?.active || 0} active</span>
            <span className="danger">{stats.users?.suspended || 0} suspended</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <h3>Car Listings</h3>
          <div className="stat-value">{stats.shifts?.total || 0}</div>
          <div className="stat-breakdown">
            <span className="active">{stats.shifts?.active || 0} active</span>
            <span className="expired">{stats.shifts?.expired || 0} expired</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🚕</div>
          <h3>Driver Listings</h3>
          <div className="stat-value">{stats.driverPosts?.total || 0}</div>
          <div className="stat-breakdown">
            <span className="active">{stats.driverPosts?.active || 0} active</span>
            <span className="expired">{stats.driverPosts?.expired || 0} expired</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🏅</div>
          <h3>Medallion Listings</h3>
          <div className="stat-value">{stats.medallionPosts?.total || 0}</div>
          <div className="stat-breakdown">
            <span className="active">{stats.medallionPosts?.active || 0} active</span>
            <span className="expired">{stats.medallionPosts?.expired || 0} expired</span>
          </div>
        </div>
      </div>

      {/* Secondary Stats Row */}
      <div className="stats-grid stats-grid-secondary">
        <div className="stat-card stat-card-small">
          <h3>New This Week</h3>
          <div className="stat-value stat-value-highlight">{stats.users?.newThisWeek || 0}</div>
          <div className="stat-breakdown">
            <span className="success">New registrations</span>
          </div>
        </div>

        <div className="stat-card stat-card-small">
          <h3>Expiring Soon</h3>
          <div className="stat-value">{stats.expiringSoon || 0}</div>
          <div className="stat-breakdown">
            <span className="warning">Within 3 days</span>
          </div>
        </div>

        <div className="stat-card stat-card-small">
          <h3>Suspended</h3>
          <div className="stat-value">{stats.users?.suspended || 0}</div>
          <div className="stat-breakdown">
            <span className="danger">Account issues</span>
          </div>
        </div>

        <div className="stat-card stat-card-small">
          <h3>Admins</h3>
          <div className="stat-value">{stats.users?.admins || 0}</div>
          <div className="stat-breakdown">
            <span className="info">System admins</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <h2 className="section-title">Activity Overview</h2>

        <ActivityChart
          data={stats.charts?.dailyActivity || []}
          title="7-Day Activity"
          emptyMessage="No activity in the last 7 days"
        />

        <div className="charts-grid">
          <PieChartCard
            data={stats.charts?.shiftsByWav || []}
            title="Car Listings by WAV"
            emptyMessage="No active shifts"
          />
          <PieChartCard
            data={stats.charts?.shiftsByType || []}
            title="Car Listings by Type"
            emptyMessage="No active shifts"
          />
          <PieChartCard
            data={stats.charts?.shiftsByVehicleType || []}
            title="Car Listings by Work Type"
            emptyMessage="No active shifts"
          />
          <PieChartCard
            data={stats.charts?.medallionsByType || []}
            title="Medallions by Type"
            emptyMessage="No active medallion listings"
          />
        </div>
      </div>
    </>
  );
};

export default AdminStatsTab;
